import { COMPANY_NAME, COPYRIGHT_YEAR, FOOTER_LEGAL_LINKS, footerLegalLines } from "../legal/company.js";
import { syncNav } from "../router.js";
import { openCommandPalette } from "./command-palette.lazy.js";
import { h, svg } from "./dom.js";
import { currentTheme, onThemeChange, themeLabel } from "./theme.js";

/**
 * The Chroma picker: a small palette icon (owner, 2026-09-23) that opens the ⌘K command line
 * straight into its theme list (`command-palette.ts`), where each theme previews as it is
 * highlighted and Enter keeps it. The list is the theme catalog (`theme.ts`: the bundled snapshot,
 * then the published catalog), grouped by family, never a hand-written menu; its label names the
 * theme as "Family · Variant"; choosing re-skins the site by setting one attribute on <html>, and
 * persists in localStorage (`storeTheme` swallows the throw when storage is blocked).
 */
export function themeButton(): HTMLElement {
  const button = h("button", { type: "button", id: "theme-button", class: "theme-button", "aria-haspopup": "dialog",
    onclick: () => openCommandPalette("theme") },
    svg("svg", { viewBox: "0 0 20 20", width: 20, height: 20, "aria-hidden": "true" },
      svg("path", { d: "M10 2.5a7.5 7.5 0 1 0 0 15c1 0 1.6-.7 1.6-1.5 0-.4-.2-.8-.4-1.1-.3-.3-.4-.7-.4-1.1 0-.9.7-1.5 1.6-1.5h1.8a3.8 3.8 0 0 0 3.8-3.8C18 5.6 14.4 2.5 10 2.5Z",
        fill: "none", stroke: "currentColor", "stroke-width": 1.5, "stroke-linejoin": "round" }),
      ...([[6.2, 9.6], [7.8, 6.4], [11.4, 5.6], [14.2, 8]] as const).map(([cx, cy]) => svg("circle", { cx, cy, r: 1.2, fill: "currentColor" }))));
  labelThemeButton(button, currentTheme());
  return button;
}

const labelThemeButton = (button: Element, id: string): void => {
  button.setAttribute("aria-label", `Theme: ${themeLabel(id)}`);
  button.setAttribute("title", `Theme: ${themeLabel(id)} (⌘K)`);
};

// ONE subscription for the app, relabelling every picker in the document. A per-button
// subscription held its button — and through the button's parents every page the visitor had
// left — in theme.ts's module-level listener set until the next theme change, which most visits
// never make: +~5 000 DOM nodes and ~3 MB per watch-page round trip (2026-09-30,
// `scripts/check-navigation-memory.mjs`).
// Querying rather than remembering keeps no button alive here, and still relabels both mastheads
// while round-route.ts's in-place round switch shows the old one and holds the new one hidden.
onThemeChange((id) => {
  for (const button of document.querySelectorAll(".theme-button")) labelThemeButton(button, id);
});

/** The masthead. One wordmark, three destinations, and the Chroma picker. */
export function masthead(): HTMLElement {
  const nav = h(
    "nav",
    { "aria-label": "Main" },
    h("a", { href: "/", text: "Tournaments" }),
    h("a", { href: "/players", text: "Players" }),
    h("a", { href: "/about", text: "About" }),
    themeButton(),
  );
  const el = h(
    "header",
    { class: "masthead" },
    h("a", { class: "wordmark", href: "/" },
      h("span", { class: "brand-symbol", "aria-hidden": "true", text: "↗" }),
      "Arrow Chess ", h("em", { text: "Live" })),
    nav,
  );
  queueMicrotask(() => syncNav(nav));
  return el;
}

/**
 * The footer: one quiet line on every page.
 *
 *   © 2026 Omega Algorithms Ltd · Privacy · Terms · Credits · Company info
 *
 * It used to be a legal filing — four paragraphs of standings maths, privacy
 * telemetry, piece-set names and the GPL — above the statutory block. Each of
 * those now lives where a reader looks for it:
 * - the standings note beside each table (`ui/standings.ts`, `ui/waiting-room.ts`),
 *   where it can state that event's own scale;
 * - the website's privacy facts on `/privacy` (`pages/legal.ts`) and `/about`;
 * - piece art, Stockfish's GPL-3 notice and its source links on `/credits`.
 *
 * The Companies Act 2006 trading disclosures stay on every page: "Company info"
 * is a `<details>` holding `footerLegalLines()`, in the DOM whether or not it
 * is open. Every string comes from `src/legal/company.ts` — one definition of
 * the company number — rather than being typed here.
 *
 * No ® or ™ on "Arrow Chess": the mark is unregistered and its clearance is
 * unfinished (`docs/legal/arrow-chess-trademark-clearance.md`).
 */
export function siteFooter(): HTMLElement {
  const links = FOOTER_LEGAL_LINKS.map((link) => h("a", { href: link.href, text: link.label }));
  return h(
    "footer",
    { class: "site-foot" },
    // A div, not a p: the Company info disclosure holds paragraphs, which a p cannot contain.
    h(
      "div",
      { class: "foot-line" },
      h("span", { text: `© ${COPYRIGHT_YEAR} ${COMPANY_NAME}` }),
      ...links.flatMap((link) => [sep(), link]),
      sep(),
      h(
        "details",
        { class: "foot-company" },
        h("summary", { text: "Company info" }),
        ...footerLegalLines().map((line) => h("p", { text: line })),
      ),
    ),
  );
}

const sep = (): HTMLElement => h("span", { class: "sep", "aria-hidden": "true", text: "·" });

/**
 * The app's schedule-empty copy, verbatim: `ScheduleView.swift:53-54` /
 * `ScheduleScreen.kt:196-197`. `Resolved(emptyList())` is an honest answer, not
 * a fault, so it gets a title + message pair rather than a warning banner.
 */
export const EMPTY_CALENDAR = {
  title: "No rounds scheduled",
  message: "Check back later for upcoming tournament broadcasts.",
} as const;

/**
 * Absence, the way the app states it: a title line and a message line. Used
 * wherever a list resolved to nothing — never for a failure, which keeps
 * `banner()`.
 */
export function emptyState(title: string, message: string): HTMLElement {
  return h(
    "div",
    { class: "empty-state", role: "status" },
    h("p", { class: "empty-state-title", text: title }),
    h("p", { class: "empty-state-message", text: message }),
  );
}

export function banner(text: string, quiet = false): HTMLElement {
  return h("p", { class: `banner${quiet ? " banner-quiet" : ""}`, role: "status", text });
}

export function sectionHead(title: string, eyebrow?: string, aside?: HTMLElement): HTMLElement {
  return h(
    "div",
    { class: "section-head" },
    h("h2", { text: title }),
    eyebrow ? h("span", { class: "eyebrow", text: eyebrow }) : null,
    aside,
  );
}
